import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setSEO } from '../utils/seo';
import { useAuthStore } from '../stores/authStore';
import { apiClient } from '../api/client';

interface UserCoupon {
  user_coupon_id: number;
  coupon_id: number;
  code: string;
  name: string;
  description: string | null;
  coupon_type: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  max_discount_amount: number | null;
  min_order_amount: number | null;
  issued_at: string;
  valid_from: string;
  valid_until: string;
  is_used: boolean;
  used_at: string | null;
  issue_source: string;
}

interface CouponsResponse {
  user_id: number;
  total_count: number;
  coupons: UserCoupon[];
}

const MyCouponsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const [coupons, setCoupons] = useState<UserCoupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'available' | 'used'>('available');
  const [couponCode, setCouponCode] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('myCoupons');

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (user?.id) {
      loadCoupons();
    }
  }, [isAuthenticated, user?.id, navigate]);

  const loadCoupons = async () => {
    if (!user?.id) {
      console.error('User ID not found:', user);
      setError(t('myCoupons.userNotFound', '사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.get<CouponsResponse>(
        `/users/${user.id}/coupons`,
        {
          params: {
            only_available: false // 모든 쿠폰 조회
          }
        }
      );

      setCoupons(response.data.coupons);
    } catch (err: any) {
      console.error('Failed to load coupons:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.detail || t('myCoupons.loadFailed', '쿠폰 목록을 불러오는데 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterCoupon = async () => {
    if (!user?.id) return;
    if (!couponCode.trim()) {
      alert(t('myCoupons.enterCode', '쿠폰 코드를 입력해주세요.'));
      return;
    }

    setIsRegistering(true);

    try {
      const response = await apiClient.post(
        `/users/${user.id}/coupons/register`,
        null,
        {
          params: {
            coupon_code: couponCode.trim()
          }
        }
      );

      alert(response.data.message || t('myCoupons.registered', '쿠폰이 등록되었습니다!'));
      setCouponCode('');
      loadCoupons(); // 쿠폰 목록 새로고침
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || t('myCoupons.registerFailed', '쿠폰 등록에 실패했습니다.');
      alert(errorMessage);
    } finally {
      setIsRegistering(false);
    }
  };

  const getFilteredCoupons = () => {
    const now = new Date();

    switch (filter) {
      case 'available':
        return coupons.filter(coupon =>
          !coupon.is_used &&
          new Date(coupon.valid_from) <= now &&
          new Date(coupon.valid_until) >= now
        );
      case 'used':
        return coupons.filter(coupon => coupon.is_used);
      default:
        return coupons;
    }
  };

  const formatDiscount = (coupon: UserCoupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}%`;
    }
    return `${coupon.discount_value.toLocaleString()}${t('common.won')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCouponTypeName = (couponType: string) => {
    const typeNames: { [key: string]: string } = {
      'payment_discount': '결제 할인',
      'delivery_discount': '배송비 할인',
      'car_seat_free': '카시트 무료',
      'insurance_discount': '보험 할인',
      'other': '기타'
    };
    const translatedTypeNames: { [key: string]: string } = {
      payment_discount: t('myCoupons.typePaymentDiscount', '결제 할인'),
      delivery_discount: t('myCoupons.typeDeliveryDiscount', '배송비 할인'),
      car_seat_free: t('myCoupons.typeCarSeatFree', '카시트 무료'),
      insurance_discount: t('myCoupons.typeInsuranceDiscount', '보험 할인'),
      other: t('myCoupons.typeOther', '기타')
    };
    return translatedTypeNames[couponType] || typeNames[couponType] || couponType;
  };

  const getStatusBadge = (coupon: UserCoupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);

    if (coupon.is_used) {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
          {t('myCoupons.used')}
        </span>
      );
    }

    if (validUntil < now) {
      return (
        <span className="px-3 py-1 bg-red-100 text-red-600 text-sm font-medium rounded-full">
          {t('myCoupons.expired', '기간만료')}
        </span>
      );
    }

    if (validFrom > now) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm font-medium rounded-full">
          {t('myCoupons.pending', '사용대기')}
        </span>
      );
    }

    return (
      <span className="px-3 py-1 bg-green-100 text-green-600 text-sm font-medium rounded-full">
        {t('myCoupons.available')}
      </span>
    );
  };

  const filteredCoupons = getFilteredCoupons();

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* 헤더 */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('myCoupons.title')}</h1>
            <p className="text-gray-600">{t('myCoupons.subtitle')}</p>
          </div>

          {/* 필터 탭 */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="flex border-b">
              <button
                onClick={() => setFilter('available')}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  filter === 'available'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('myCoupons.available')}
              </button>
              <button
                onClick={() => setFilter('used')}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  filter === 'used'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('myCoupons.used')}
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  filter === 'all'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('myCoupons.all')}
              </button>
            </div>
          </div>

          {/* 쿠폰 코드 등록 */}
          <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('myCoupons.registerTitle')}</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && !isRegistering && handleRegisterCoupon()}
                placeholder={t('myCoupons.codePlaceholder')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isRegistering}
              />
              <button
                onClick={handleRegisterCoupon}
                disabled={isRegistering || !couponCode.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isRegistering ? t('myCoupons.registering') : t('myCoupons.register')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t('myCoupons.registerHelp')}
            </p>
          </div>

          {/* 로딩 상태 */}
          {isLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">{t('myCoupons.loading')}</p>
            </div>
          )}

          {/* 에러 상태 */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadCoupons}
                className="mt-2 text-red-600 hover:text-red-700 underline"
              >
                {t('myCoupons.retry')}
              </button>
            </div>
          )}

          {/* 쿠폰 목록 */}
          {!isLoading && !error && (
            <>
              {filteredCoupons.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                    />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    {filter === 'available' && t('myCoupons.emptyAvailable')}
                    {filter === 'used' && t('myCoupons.emptyUsed')}
                    {filter === 'all' && t('myCoupons.emptyAll')}
                  </h3>
                  <p className="mt-2 text-gray-500">
                    {t('myCoupons.emptyDesc')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCoupons.map((coupon) => (
                    <div
                      key={coupon.user_coupon_id}
                      className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                        coupon.is_used || new Date(coupon.valid_until) < new Date()
                          ? 'opacity-60'
                          : ''
                      }`}
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">
                                {coupon.name}
                              </h3>
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                                {getCouponTypeName(coupon.coupon_type)}
                              </span>
                            </div>
                            {coupon.description && (
                              <p className="text-gray-600 text-sm">{coupon.description}</p>
                            )}
                          </div>
                          {getStatusBadge(coupon)}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">{t('myCoupons.discountAmount')}</p>
                            <p className="text-xl font-bold text-blue-600">
                              {formatDiscount(coupon)}
                            </p>
                            {coupon.discount_type === 'percentage' && coupon.max_discount_amount && (
                              <p className="text-xs text-gray-500 mt-1">
                                {t('myCoupons.max')} {coupon.max_discount_amount.toLocaleString()}{t('common.won')}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">{t('myCoupons.minOrderAmount')}</p>
                            <p className="text-base font-medium text-gray-900">
                              {coupon.min_order_amount
                                ? `${coupon.min_order_amount.toLocaleString()}${t('common.won')} ${t('myCoupons.orMore')}`
                                : t('myCoupons.noLimit')}
                            </p>
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <div className="flex justify-between text-sm">
                            <div>
                              <span className="text-gray-500">{t('myCoupons.issuedAt')}:</span>{' '}
                              <span className="text-gray-900">{formatDate(coupon.issued_at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('myCoupons.validPeriod')}:</span>{' '}
                              <span className="text-gray-900">
                                {formatDate(coupon.valid_from)} ~ {formatDate(coupon.valid_until)}
                              </span>
                            </div>
                          </div>
                          {coupon.is_used && coupon.used_at && (
                            <div className="mt-2 text-sm">
                              <span className="text-gray-500">{t('myCoupons.usedAt')}:</span>{' '}
                              <span className="text-gray-900">{formatDate(coupon.used_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 요약 정보 */}
              {coupons.length > 0 && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-900">
                      {t('myCoupons.totalCoupons', { count: coupons.length })}
                    </span>
                    <span className="text-sm text-blue-700">
                      {t('myCoupons.availableCount', { count: coupons.filter(c => !c.is_used && new Date(c.valid_from) <= new Date() && new Date(c.valid_until) >= new Date()).length })}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* 예약 페이지로 이동 버튼 */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/reservation')}
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('myCoupons.viewReservations')}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyCouponsPage;
